package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * 元素结构校验。
 *
 * - kind 必须在 [ALLOWED_KINDS] 白名单内
 * - id 必须匹配 [ID_PATTERN]
 * - 同一父级下子元素 id 不可重复
 * - modal / toast 必须显式声明 visible 属性（E750）
 * - toast kind / position / duration 字面量取值校验（E752 / E753 / E754）
 * - button variant 字符串字面量取值校验（E404）
 * - text typography 字符串字面量取值校验（E405）
 *
 * @property knownComponentNames IDE 单文件分析时传入的"项目级已知 component 名"白名单。
 *   遇到命中此集合的元素 kind 时跳过 E401 / id 校验 / 子树校验——这是跨文件 component 调用，
 *   按内置元素 kind 规则校验会产生大量假阳性。其余检查（modal visible / toast 字面量等）
 *   反正只对内置 kind 触发，自然不影响。
 */
class ElementValidator(
    private val diag: DiagnosticCollector,
    private val knownComponentNames: Set<String> = emptySet(),
) {

    fun validate(root: ElementAst) {
        validateElement(root, conditionalDepth = 0, inVirtualList = false)
    }

    private fun validateElement(element: ElementAst, conditionalDepth: Int, inVirtualList: Boolean) {
        // RFC-RECOMPOSITION：kind=$cond 是编译期生成的包装节点，不参与 kind 白名单 / id 命名校验。
        // 直接校验 conditionalContent 后递归 branches / elseBranch。
        if (element.conditionalContent != null) {
            // E844：virtualList 内的 conditional 与窗口化复用语义冲突，编译期拒绝。
            // 只检测"直接放在 virtualList 子树内"——virtualList → 模板内嵌套 conditional 是允许的，
            // 因为模板会被独立实例化为窗口子项，每个窗口子项内的 conditional 各自工作。
            // 所以只在 virtualList 直接子层级才报 E844；本检测通过 inVirtualList 标志在递归时
            // 按"是否当前位于 virtualList 直接子层级"控制。
            if (inVirtualList) {
                diag.error(DslError.E844_CONDITIONAL_INSIDE_VIRTUAL_LIST, element.position)
            }
            validateConditionalContent(element, conditionalDepth, inVirtualList)
            return
        }

        // $for：kind=$for 同样是编译期生成的包装节点，不参与 kind 白名单 / id 命名校验。
        // 校验 forContent 后递归循环体；与 conditional 共享 conditionalDepth 嵌套预算。
        if (element.forContent != null) {
            // E847：virtualList 内的 for 与窗口化复用语义冲突，编译期拒绝（仅直接子层级）。
            if (inVirtualList) {
                diag.error(DslError.E847_FOR_INSIDE_VIRTUAL_LIST, element.position)
            }
            validateForContent(element, conditionalDepth, inVirtualList)
            return
        }

        // IDE 单文件分析路径：跨文件 component 引用（kind 命中白名单）跳过整个子树校验——
        // 它不是内置元素 kind，按内置规则校验会产生 E401 / E402 等大量假阳性。
        // ScreenWithComponentsPipeline 多文件编译会展开此类调用为内置元素树后再校验。
        if (element.kind in knownComponentNames) {
            return
        }

        // kind 白名单
        if (element.kind !in ALLOWED_KINDS) {
            diag.error(DslError.E401_UNKNOWN_KIND, element.position, element.kind)
        }

        // id 命名。menu item / divider 是 overlay 专用伪元素，解析期允许无显式 id。
        if (element.kind !in MENU_CHILD_KINDS && (element.id.isEmpty() || !ID_PATTERN.matches(element.id))) {
            diag.error(DslError.E402_ILLEGAL_ID, element.position, element.id)
        }

        // 同级 id 唯一
        val seen = mutableSetOf<String>()
        element.children.forEach { child ->
            if (!seen.add(child.id)) {
                diag.error(DslError.E403_DUPLICATE_SIBLING_ID, child.position, child.id)
            }
        }

        // modal / toast 弹层校验
        when (element.kind) {
            "modal" -> checkVisible(element)
            "toast" -> {
                checkVisible(element)
                checkToastContentMutex(element)
                checkToastLiterals(element)
            }
            "button" -> checkButtonLiterals(element)
            "text" -> checkTextLiterals(element)
        }

        // 递归
        // RFC-RECOMPOSITION：menuBlocks 路径与 children 同级递归；conditional 分支由 validateConditionalContent
        // 入口处理（独立带 depth 计数 + E840 检查），不放到这里递归避免重复 / 错误深度。
        // 当本 element 是 virtualList 时，对其直接子元素（children + menuBlocks）传入 inVirtualList=true，
        // 让 conditional 被 E844 拦截；其他 element 透传当前 inVirtualList 状态（一旦离开 virtualList 即清零）。
        val childInVirtualList = element.kind == "virtualList"
        element.children.forEach { validateElement(it, conditionalDepth, childInVirtualList) }
        element.menuBlocks.forEach { menu -> validateElement(menu.root, conditionalDepth, childInVirtualList) }
    }

    /**
     * RFC-RECOMPOSITION：校验 conditional 包装节点。
     *
     * - E842：嵌套深度超过 [MAX_CONDITIONAL_NESTING]
     * - E840：分支体内含 inventory / slotGrid 直接报错（DFS 子树）
     * - 递归 validate 每个 branch.body 与 elseBranch
     * - 进入分支体后 inVirtualList 清零：分支体一旦实例化即作为独立子树（窗口化语义不再适用）
     */
    private fun validateConditionalContent(element: ElementAst, conditionalDepth: Int, inVirtualList: Boolean) {
        if (conditionalDepth >= MAX_CONDITIONAL_NESTING) {
            diag.error(DslError.E842_CONDITIONAL_NESTING_TOO_DEEP, element.position, MAX_CONDITIONAL_NESTING)
        }
        val content = element.conditionalContent ?: return
        for (branch in content.branches) {
            checkNoInventoryInSubtree(branch.body, element.position)
            validateElement(branch.body, conditionalDepth + 1, inVirtualList = false)
        }
        content.elseBranch?.let {
            checkNoInventoryInSubtree(it, element.position)
            validateElement(it, conditionalDepth + 1, inVirtualList = false)
        }
    }

    /** E840：DFS 子树查找 inventory / slotGrid，命中即报错（仅在最近祖先 conditional 位置报告一次）。 */
    private fun checkNoInventoryInSubtree(root: ElementAst, conditionalPos: top.wcpe.allincore.dsl.compiler.source.SourcePosition) {
        if (root.kind == "inventory" || root.kind == "slotGrid") {
            diag.error(DslError.E840_INVENTORY_IN_CONDITIONAL, conditionalPos, root.kind)
            return
        }
        // 进入嵌套 conditional / for 后停止 inventory 检查（它们自己会校验各自子树）
        if (root.conditionalContent != null || root.forContent != null) return
        for (child in root.children) {
            checkNoInventoryInSubtree(child, conditionalPos)
        }
    }

    /**
     * $for：校验 for 包装节点。
     *
     * - E846：嵌套深度超过 [MAX_CONDITIONAL_NESTING]（与 conditional 共享预算）
     * - E848：循环体内含 inventory / slotGrid 直接报错（DFS 子树）
     * - E849：Map 解构形态同时指定显式 key 子句，编译期拒绝
     * - 递归 validate 循环体（单根），进入后 inVirtualList 清零
     */
    private fun validateForContent(element: ElementAst, conditionalDepth: Int, inVirtualList: Boolean) {
        if (conditionalDepth >= MAX_CONDITIONAL_NESTING) {
            diag.error(DslError.E846_FOR_NESTING_TOO_DEEP, element.position, MAX_CONDITIONAL_NESTING)
        }
        val content = element.forContent ?: return
        if (content.overMap && content.keyExpr != null) {
            diag.error(DslError.E849_FOR_MAP_EXPLICIT_KEY, content.keyExpr.position)
        }
        checkNoInventoryInForSubtree(content.body, element.position)
        validateElement(content.body, conditionalDepth + 1, inVirtualList = false)
    }

    /** E848：DFS 子树查找 inventory / slotGrid，命中即报错（仅在最近祖先 for 位置报告一次）。 */
    private fun checkNoInventoryInForSubtree(root: ElementAst, forPos: top.wcpe.allincore.dsl.compiler.source.SourcePosition) {
        if (root.kind == "inventory" || root.kind == "slotGrid") {
            diag.error(DslError.E848_INVENTORY_IN_FOR, forPos, root.kind)
            return
        }
        // 进入嵌套 conditional / for 后停止（它们自己会校验各自子树）
        if (root.conditionalContent != null || root.forContent != null) return
        for (child in root.children) {
            checkNoInventoryInForSubtree(child, forPos)
        }
    }

    /** E750：modal / toast 必须显式声明 visible 属性。 */
    private fun checkVisible(el: ElementAst) {
        if (el.props.none { it.name == "visible" }) {
            diag.error(DslError.E750_MODAL_DIALOG_TOAST_VISIBLE_REQUIRED, el.position, el.kind, el.id)
        }
    }

    /** E740：toast 的 message 与富内容互斥。 */
    private fun checkToastContentMutex(el: ElementAst) {
        if (el.toastContent != null && el.props.any { it.name == "message" }) {
            diag.error(DslError.E740_TOAST_MESSAGE_CONTENT_MUTEX, el.position)
        }
    }

    /** E752 / E753 / E754：toast kind / position / duration 字面量取值校验。 */
    private fun checkToastLiterals(el: ElementAst) {
        for (p in el.props) {
            when (p.name) {
                "kind" -> {
                    val v = p.value as? ExprAst.Literal.StringLit ?: continue
                    if (v.value !in TOAST_KINDS) {
                        diag.error(DslError.E752_TOAST_KIND_UNKNOWN, p.position, v.value)
                    }
                }
                "position" -> {
                    val v = p.value as? ExprAst.Literal.StringLit ?: continue
                    if (v.value !in TOAST_POSITIONS) {
                        diag.error(DslError.E753_TOAST_POSITION_UNKNOWN, p.position, v.value)
                    }
                }
                "duration" -> {
                    val lit = p.value as? ExprAst.Literal ?: continue
                    val invalid = when (lit) {
                        is ExprAst.Literal.NumberLit -> lit.value.toDouble() < 0.0
                        else -> true
                    }
                    if (invalid) {
                        diag.error(DslError.E754_TOAST_DURATION_INVALID, p.position, describeLiteral(lit))
                    }
                }
            }
        }
    }

    /** E404：button variant 字符串字面量取值校验；动态表达式交由运行期处理。 */
    private fun checkButtonLiterals(el: ElementAst) {
        for (p in el.props) {
            if (p.name != "variant") continue
            val v = p.value as? ExprAst.Literal.StringLit ?: continue
            if (v.value !in BUTTON_VARIANTS) {
                diag.error(DslError.E404_BUTTON_VARIANT_UNKNOWN, p.position, v.value)
            }
        }
    }

    /** E405：text typography 字符串字面量取值校验；动态表达式交由运行期处理。 */
    private fun checkTextLiterals(el: ElementAst) {
        for (p in el.props) {
            if (p.name != "typography") continue
            val v = p.value as? ExprAst.Literal.StringLit ?: continue
            if (v.value !in TEXT_TYPOGRAPHY_ROLES) {
                diag.error(DslError.E405_TEXT_TYPOGRAPHY_UNKNOWN, p.position, v.value)
            }
        }
    }

    /** 字面量诊断时的可读描述。 */
    private fun describeLiteral(lit: ExprAst.Literal): String = when (lit) {
        is ExprAst.Literal.NumberLit -> lit.value.toString()
        is ExprAst.Literal.StringLit -> "String"
        is ExprAst.Literal.BoolLit -> "Boolean"
    }

    companion object {
        /** RFC-RECOMPOSITION：conditional 嵌套深度上限，超出报 E842。 */
        const val MAX_CONDITIONAL_NESTING: Int = 8

        /**
         * 元素 kind 白名单；与运行时 IrElementFactory 注册保持一致。
         *
         * 实际定义在 [top.wcpe.allincore.dsl.compiler.api.IdeMetadata.allElementKinds]，
         * 本字段仅做向后兼容别名（已有外部测试引用 `ElementValidator.ALLOWED_KINDS`）。
         */
        val ALLOWED_KINDS: Set<String> = top.wcpe.allincore.dsl.compiler.api.IdeMetadata.allElementKinds

        /** 菜单 overlay 解析专用子元素。 */
        private val MENU_CHILD_KINDS = top.wcpe.allincore.dsl.compiler.api.IdeMetadata.menuChildKinds

        /** toast kind 合法取值。 */
        private val TOAST_KINDS = top.wcpe.allincore.dsl.compiler.api.IdeMetadata.toastKinds

        /** toast position 合法取值。 */
        private val TOAST_POSITIONS = top.wcpe.allincore.dsl.compiler.api.IdeMetadata.toastPositions

        /** button variant 合法取值。 */
        private val BUTTON_VARIANTS = top.wcpe.allincore.dsl.compiler.api.IdeMetadata.buttonVariants

        /** text typography 合法取值。 */
        private val TEXT_TYPOGRAPHY_ROLES = top.wcpe.allincore.dsl.compiler.api.IdeMetadata.textTypographyRoles

        /** 合法 id 正则：字母或下划线开头，后续允许字母/数字/下划线/短横线；组件展开后可含 `/` 分隔的多段前缀。 */
        private val ID_PATTERN = Regex("[A-Za-z_][A-Za-z0-9_-]*(/[A-Za-z_][A-Za-z0-9_-]*)*")
    }
}
